import React, { useMemo, useState } from "react";
import { Button, CircularProgress, Grid, TextField } from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import dayjs from "dayjs";
import { motion } from "framer-motion";
import { toast } from "react-toastify";
import axiosInterceptorInstance from "../../../utils/axios.interceptor";

const PAGE_SIZE_OPTIONS = [10, 25, 50];

const COLUMN_HEADERS: Record<string, string> = {
  "product.productName": "Product Name",
  "product.countryCode": "Product Country Code",
  "product.currencyCode": "Product Currency Code",
  "product.brand.brandName": "Brand Name",
};

const HIDDEN_FIELDS = new Set(["product.brand.brandId"]);

const flattenRow = (row: any, prefix = "", result: any = {}) => {
  Object.entries(row || {}).forEach(([key, value]) => {
    const field = prefix ? `${prefix}.${key}` : key;
    const isHidden =
      HIDDEN_FIELDS.has(field) ||
      Array.from(HIDDEN_FIELDS).some((hiddenField) =>
        field.startsWith(`${hiddenField}.`),
      );

    if (isHidden) return;

    if (value && typeof value === "object" && !Array.isArray(value)) {
      flattenRow(value, field, result);
    } else {
      result[field] = Array.isArray(value) ? value.join(", ") : value;
    }
  });

  return result;
};

const headerFromField = (field: string) =>
  COLUMN_HEADERS[field] ??
  field
    .replace(/\./g, " ")
    .replace(/_/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/^./, (letter) => letter.toUpperCase());

const ReloadlyTransactions = () => {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [customIdentifier, setCustomIdentifier] = useState("");
  const [rows, setRows] = useState<any[]>([]);
  const [rowCount, setRowCount] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [searchLoading, setSearchLoading] = useState(false);
  const [gridLoading, setGridLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const loadTransactions = async (
    requestedPage = page,
    requestedPageSize = pageSize,
    isNewSearch = false,
  ) => {
    if (isNewSearch) {
      setRows([]);
      setRowCount(0);
      setHasSearched(false);
      setSearchLoading(true);
    } else {
      setRows([]);
      setGridLoading(true);
    }
    toast.dismiss();

    try {
      const response = await axiosInterceptorInstance.get(
        "/FusionHubCrm/GetReloadlyTransactions",
        {
          params: {
            Size: requestedPageSize,
            Page: requestedPage + 1,
            StartDate: startDate,
            ...(endDate && { EndDate: endDate }),
            ...(customIdentifier.trim() && {
              CustomIdentifier: customIdentifier.trim(),
            }),
          },
        },
      );

      const records = response.data?.payload ?? [];

      setRows(
        records.map((record: any, index: number) => ({
          ...flattenRow(record),
          id:
            record.transactionId ??
            record.customIdentifier ??
            `${requestedPage}-${index}`,
        })),
      );
      setRowCount(
        requestedPage * requestedPageSize +
          records.length +
          (records.length === requestedPageSize ? 1 : 0),
      );
      setHasSearched(true);

      if (records.length === 0) toast.info("No records found.");
    } catch {
      setRows([]);
      setRowCount(0);
    } finally {
      if (isNewSearch) setSearchLoading(false);
      else setGridLoading(false);
    }
  };

  const columns = useMemo<GridColDef[]>(() => {
    if (!rows.length) return [];

    return Object.keys(rows[0])
      .filter((field) => field !== "id")
      .map((field) => ({
        field,
        headerName: headerFromField(field),
        minWidth: 170,
        flex: 1,
        sortable: true,
      }));
  }, [rows]);

  const search = () => {
    toast.dismiss();
    if (!startDate) {
      toast.error("Please select a start date.");
      return;
    }

    setPage(0);
    loadTransactions(0, pageSize, true);
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <div className="boxShadow">
        <h2 className="sectionTitle">Reloadly Transactions</h2>
        <div className="boxInner">
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                required
                label="Start Date"
                type="date"
                value={startDate}
                onChange={(event) => {
                  setStartDate(event.target.value);
                  if (endDate && event.target.value > endDate) setEndDate("");
                }}
                InputLabelProps={{ shrink: true }}
                inputProps={{ max: dayjs().format("YYYY-MM-DD") }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="End Date"
                type="date"
                value={endDate}
                onChange={(event) => setEndDate(event.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  min: startDate || undefined,
                  max: dayjs().format("YYYY-MM-DD"),
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Custom Identifier"
                value={customIdentifier}
                onChange={(event) => setCustomIdentifier(event.target.value)}
              />
            </Grid>
            <Grid item xs={4} style={{ margin: "0 auto" }}>
              <Button
                fullWidth
                variant="contained"
                className="dButton dButton2"
                onClick={search}
                disabled={searchLoading || gridLoading}
                sx={{ height: 56 }}
              >
                {searchLoading ? (
                  <CircularProgress color="inherit" size={28} />
                ) : (
                  "Search"
                )}
              </Button>
            </Grid>
          </Grid>
        </div>
      </div>

      {hasSearched && (rows.length > 0 || gridLoading) && (
        <div style={{ height: 560, width: "100%", marginTop: 16 }}>
          <DataGrid
            rows={rows}
            columns={columns}
            loading={gridLoading}
            pagination
            paginationMode="server"
            rowCount={rowCount}
            page={page}
            pageSize={pageSize}
            rowsPerPageOptions={PAGE_SIZE_OPTIONS}
            onPageChange={(newPage) => {
              setPage(newPage);
              loadTransactions(newPage, pageSize);
            }}
            onPageSizeChange={(newPageSize) => {
              setPage(0);
              setPageSize(newPageSize);
              loadTransactions(0, newPageSize);
            }}
            disableSelectionOnClick
          />
        </div>
      )}
    </motion.div>
  );
};

export default ReloadlyTransactions;
